import { useRef, useState } from "react";
import { Author } from "../types/community";

type LikersLoader = () => Promise<Author[] | null>;

/**
 * State for the "who liked this" modal shared by posts and comments.
 * `open(title, loader)` shows the modal and fetches; `retry()` re-runs the
 * last loader so the modal's retry button works after a failure.
 */
export function useLikers() {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [likers, setLikers] = useState<Author[]>([]);
  const [title, setTitle] = useState("Likes");
  const loaderRef = useRef<LikersLoader | null>(null);

  const load = async (loader: LikersLoader) => {
    loaderRef.current = loader;
    setError(null);
    setLoading(true);
    try {
      const result = await loader();
      if (result) {
        setLikers(result);
      } else {
        setLikers([]);
        setError("Failed to load likes. Please try again.");
      }
    } catch (err) {
      setLikers([]);
      setError("An unexpected error occurred while loading likes.");
    } finally {
      setLoading(false);
    }
  };

  const open = async (modalTitle: string, loader: LikersLoader) => {
    setTitle(modalTitle);
    setIsOpen(true);
    await load(loader);
  };

  const close = () => setIsOpen(false);

  const retry = () => {
    if (loaderRef.current) {
      load(loaderRef.current);
    }
  };

  return { isOpen, loading, error, likers, title, open, close, retry };
}
